import type * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

interface ConfirmDialogProps {
  title: React.ReactNode;
  description: React.ReactNode;
  /** The affirmative button. Name the action — "Delete", "Hand it in" — never "OK". */
  confirmLabel: React.ReactNode;
  onConfirm: () => void;
  /** Defaults to "Cancel". Say what staying put means when that reads better ("Keep editing"). */
  cancelLabel?: React.ReactNode;
  /** Red confirm button. For anything that destroys or unrecoverably ends something. */
  destructive?: boolean;
  /**
   * Controlled form: the caller owns the open state and typically renders this
   * only while there is a target. Omit both and pass `trigger` instead for the
   * uncontrolled form.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Uncontrolled form: the element that opens the dialog, cloned as the trigger. */
  trigger?: React.ReactNode;
}

/**
 * The one "are you sure?" dialog. Every confirmation in Enable goes through it so
 * the destructive styling, the button order and the cancel default can't drift —
 * they were retyped in thirteen files before this existed.
 *
 * Not for a three-way choice (save / discard / keep editing) — that is a different
 * question and wants its own dialog.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  cancelLabel = "Cancel",
  destructive = false,
  open,
  onOpenChange,
  trigger,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger> : null}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={destructive ? buttonVariants({ variant: "destructive" }) : undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
